/**
 * StatTile — labelled metric primitive (design-revamp / handoff-2026-06).
 *
 * Production translation of the prototype `StatTile`
 * (`docs/GardEn/handoff-2026-06/garden/ui.jsx`): a small uppercase micro-
 * label, a large tabular-nums value, and an optional sub-line.
 *
 * `onPrimary` re-tones the tile for placement on a colored band (e.g. inside
 * a HeroBand): the value uses `--g-on-primary` and the label/sub use the
 * translucent on-primary tints the prototype hard-codes as
 * `rgba(252,250,246,.7)` / `.65`. Here those are expressed as
 * `text-[var(--g-on-primary)]/70` etc. so the colour still flows from the
 * token, not a literal.
 */
import type { ReactElement, ReactNode } from "react";

export type StatTileAlign = "left" | "center" | "right";

export interface StatTileProps {
  /** Micro-label above the value (rendered uppercase). */
  label: string;
  /** The metric value (string or pre-formatted node). */
  value: ReactNode;
  /** Optional secondary line below the value. */
  sub?: ReactNode;
  /** Re-tone for placement on a colored/primary band. */
  onPrimary?: boolean;
  /** Text alignment. Defaults to `left`. */
  align?: StatTileAlign;
}

const alignClass: Record<StatTileAlign, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

/**
 * Renders a stat tile. `data-testid="g-stat-tile"`; the value carries
 * `g-stat-tile-value` for assertions.
 */
export function StatTile({
  label,
  value,
  sub,
  onPrimary = false,
  align = "left",
}: StatTileProps): ReactElement {
  const labelTone = onPrimary
    ? "text-[var(--g-on-primary)]/70"
    : "text-[var(--g-ink-3)]";
  const valueTone = onPrimary
    ? "text-[var(--g-on-primary)]"
    : "text-foreground";
  const subTone = onPrimary
    ? "text-[var(--g-on-primary)]/65"
    : "text-[var(--g-ink-2)]";

  return (
    <div data-testid="g-stat-tile" className={alignClass[align]}>
      <div
        className={[
          "mb-1 text-[10.5px] font-bold uppercase tracking-[0.04em]",
          labelTone,
        ].join(" ")}
      >
        {label}
      </div>
      <div
        data-testid="g-stat-tile-value"
        className={[
          "text-[26px] font-bold leading-none tracking-[-0.02em] tabular-nums",
          valueTone,
        ].join(" ")}
      >
        {value}
      </div>
      {sub && (
        <div className={["mt-1 text-xs", subTone].join(" ")}>{sub}</div>
      )}
    </div>
  );
}
